/**
 * ContractProjectTypesSettingsAdapter
 *
 * Pure utility — translates the Settings blob on a ContractType / ProjectType
 * record between two representations:
 *
 *   - **Storage form** (what the API sends/accepts): native JS types inside
 *     `record.Settings` — arrays of strings for the list fields and plain
 *     strings for scalars.
 *
 *   - **Display form** (what the Settings editor binds to): the same scalar /
 *     boolean values, plus newline-joined text for the list fields. Stored on
 *     `record._DisplaySettings` so the edit form's descriptors can bind to those
 *     addresses without polluting the actual Settings blob the API stores.
 *
 * Two single-purpose functions:
 *
 *   - `toDisplay(record)` — populates `record._DisplaySettings.*` from
 *     `record.Settings.*`. Idempotent. Safe on records that have no Settings.
 *   - `fromDisplay(record)` — overwrites `record.Settings.*` with parsed values
 *     from `record._DisplaySettings.*`, then deletes `_DisplaySettings` so the
 *     adapter's shadow never reaches the API.
 *
 * Designed so the PSRS MeadowEndpoints provider can be lightly wrapped in
 * `PlatformConfiguration-Application-Web.js`: call `toDisplay` after fetch,
 * `fromDisplay` before create/update. The adapter has no Fable / pict
 * dependencies and is unit-tested in isolation.
 */

/**
 * Keys whose Settings value is an array of strings and whose display form is
 * newline-joined text (driven by a TextArea descriptor in the edit manifest).
 *
 * NOTE: the status lists used to live here. They are now stored canonically as
 * ListDefault rows (see STATUS_LIST_KEYS below), so this list is currently
 * empty — kept as the extension point for any future newline-text Settings list.
 */
const LIST_KEYS = [];

/**
 * Status list display keys. These are NOT round-tripped through the Settings
 * blob — statuses are stored canonically as ListDefault rows and synced via the
 * `/1.0/{ProjectType|ContractType}/:id/Statuses` endpoints. They surface in the
 * editor as a comma-joined value bound to `_DisplaySettings.*` — the wire format of
 * the status `Select2Multiple` (free-text chip) inputs; the PSRS wiring in
 * PlatformConfiguration-Application-Web.js fills them from the /Statuses endpoint
 * on load and writes them back on save. `toDisplay` initializes them to '' so the
 * manifest has stable binding targets, and `fromDisplay` strips any legacy values
 * from Settings so they never persist there again.
 */
const STATUS_LIST_KEYS = [
	'AvailableStatusList',
	'StatusesRequiringChangeOrder',
];

/**
 * Map each `_DisplaySettings` status key to the purpose used by the /Statuses
 * endpoint payload ({ Available, RequiresChangeOrder }).
 */
const STATUS_DISPLAY_KEY_TO_PURPOSE = {
	AvailableStatusList: 'Available',
	StatusesRequiringChangeOrder: 'RequiresChangeOrder',
};

/**
 * Keys whose Settings value is an array of strings but whose display form is
 * comma-separated text — what the bundled `EntitySelectorMultiple` /
 * `Select2Multiple` inputs read and write as their bound value. These are the
 * fields rendered as a true multi-select chip control, not a free-typed text
 * area.
 */
const CSV_LIST_KEYS = [];

/**
 * Display keys backed by a dedicated non-CRUD endpoint (NOT the Settings blob),
 * rendered as a comma-separated multi-select value. Like STATUS_LIST_KEYS these are
 * display-only: `toDisplay` initializes them empty (the PSRS wiring fills them from
 * their endpoint on load), and `fromDisplay` strips them from Settings so they never
 * persist there. `AllowedProjectTypeIDs` is stored as ListDefault rows via the
 * `/ContractType/:id/AllowedProjectTypes` endpoint (same model as statuses).
 * `AllowedProjectTypeNamesText` is the read-only View mirror (resolved Names); it is
 * never saved.
 */
const ENDPOINT_CSV_KEYS = [
	'AllowedProjectTypeIDs',
	'AllowedProjectTypeNamesText',
];

/**
 * Convert a string-array Settings value into a newline-joined text string for
 * the display textarea.
 *
 * @param {Array<string> | null | undefined} pList
 * @returns {string}
 */
function listToText(pList)
{
	if (!Array.isArray(pList))
	{
		return '';
	}
	return pList
		.filter((p) => p !== null && p !== undefined && String(p).length > 0)
		.join('\n');
}

/**
 * Parse a newline-separated textarea value back into an array of trimmed
 * strings. Blank lines are dropped.
 *
 * @param {string | null | undefined} pText
 * @returns {Array<string>}
 */
function textToList(pText)
{
	if (typeof pText !== 'string' || pText.length === 0)
	{
		return [];
	}
	return pText
		.split(/\r?\n/)
		.map((pLine) => pLine.trim())
		.filter((pLine) => pLine.length > 0);
}

/**
 * Convert a string-array Settings value into a comma-separated string — the
 * wire format used by `Select2Multiple` / `EntitySelectorMultiple` bound values.
 *
 * @param {Array<string> | null | undefined} pList
 * @returns {string}
 */
function listToCsv(pList)
{
	if (!Array.isArray(pList))
	{
		return '';
	}
	return pList
		.filter((p) => p !== null && p !== undefined && String(p).length > 0)
		.map((p) => String(p).trim())
		.filter((p) => p.length > 0)
		.join(',');
}

/**
 * Parse a comma-separated multi-select value back into an array of trimmed
 * strings. Blank entries are dropped.
 *
 * @param {string | Array<string> | null | undefined} pValue
 * @returns {Array<string>}
 */
function csvToList(pValue)
{
	if (Array.isArray(pValue))
	{
		// Belt-and-suspenders: if the input is already shaped as an array
		// (e.g. the multi-select widget marshaled a real array through), accept
		// it as-is. Falls through cleanly when the value is just a CSV string.
		return pValue.map((p) => String(p).trim()).filter((p) => p.length > 0);
	}
	if (typeof pValue !== 'string' || pValue.length === 0)
	{
		return [];
	}
	return pValue
		.split(',')
		.map((pEntry) => pEntry.trim())
		.filter((pEntry) => pEntry.length > 0);
}

/**
 * Convert the structured `DefaultDates` array into a `HASH:Name` per-line text.
 *
 * @param {Array<{Hash?: string, Name?: string}> | null | undefined} pDates
 * @returns {string}
 */
function defaultDatesToText(pDates)
{
	if (!Array.isArray(pDates))
	{
		return '';
	}
	return pDates
		.filter((pEntry) => pEntry && (pEntry.Hash || pEntry.Name))
		.map((pEntry) =>
		{
			const tmpHash = (pEntry.Hash || '').toString().trim();
			const tmpName = (pEntry.Name || '').toString().trim();
			if (tmpHash && tmpName)
			{
				return `${tmpHash}:${tmpName}`;
			}
			return tmpHash || tmpName;
		})
		.join('\n');
}

/**
 * Parse `HASH:Name` per-line text into the structured DefaultDates array.
 * Lines without a colon are treated as Hash-only (Name copied from Hash).
 *
 * @param {string | null | undefined} pText
 * @returns {Array<{Hash: string, Name: string}>}
 */
function textToDefaultDates(pText)
{
	if (typeof pText !== 'string' || pText.length === 0)
	{
		return [];
	}
	return pText
		.split(/\r?\n/)
		.map((pLine) => pLine.trim())
		.filter((pLine) => pLine.length > 0)
		.map((pLine) =>
		{
			const tmpColonIdx = pLine.indexOf(':');
			if (tmpColonIdx === -1)
			{
				return { Hash: pLine, Name: pLine };
			}
			return {
				Hash: pLine.substring(0, tmpColonIdx).trim(),
				Name: pLine.substring(tmpColonIdx + 1).trim(),
			};
		});
}

/**
 * Populate `record._DisplaySettings.*` from `record.Settings.*`. Idempotent.
 *
 * @param {Object | null | undefined} pRecord - The ContractType/ProjectType record.
 * @returns {Object | null | undefined} The same record, mutated for chaining.
 */
function toDisplay(pRecord)
{
	if (!pRecord || typeof pRecord !== 'object')
	{
		return pRecord;
	}
	const tmpSettings = (pRecord.Settings && typeof pRecord.Settings === 'object') ? pRecord.Settings : {};
	const tmpDisplay = {};
	for (const tmpKey of LIST_KEYS)
	{
		tmpDisplay[tmpKey] = listToText(tmpSettings[tmpKey]);
	}
	// Statuses live in ListDefault, not Settings — initialize the display targets
	// to empty strings (stable binding targets); the PSRS wiring fills them from
	// the /Statuses endpoint after this runs.
	for (const tmpKey of STATUS_LIST_KEYS)
	{
		tmpDisplay[tmpKey] = '';
	}
	for (const tmpKey of CSV_LIST_KEYS)
	{
		tmpDisplay[tmpKey] = listToCsv(tmpSettings[tmpKey]);
	}
	// Endpoint-backed multi-selects (e.g. AllowedProjectTypeIDs) initialize empty;
	// the PSRS wiring fills them from their endpoint (e.g. /AllowedProjectTypes) after this runs.
	for (const tmpKey of ENDPOINT_CSV_KEYS)
	{
		tmpDisplay[tmpKey] = '';
	}
	// DefaultDates live as ProjectTypeDefaultDate / ContractTypeDefaultDate rows
	// Initialize the display
	// target empty; the PSRS wiring fills it from the /DefaultDates endpoint after
	// this runs (the Tabular RecordSet group binds to _DisplaySettings.DefaultDates).
	tmpDisplay.DefaultDates = [];
	// Prototype project/contract name are display-only too: initialize empty binding
	// targets; the PSRS read wiring fills them after this runs.
	tmpDisplay.PrototypeProjectName = '';
	tmpDisplay.PrototypeContractName = '';
	pRecord._DisplaySettings = tmpDisplay;
	return pRecord;
}

/**
 * Overwrite `record.Settings.*` with parsed values from
 * `record._DisplaySettings.*`, then delete `_DisplaySettings`. Idempotent on
 * records that already have no `_DisplaySettings`.
 *
 * @param {Object | null | undefined} pRecord
 * @returns {Object | null | undefined} The same record, mutated for chaining.
 */
function fromDisplay(pRecord)
{
	if (!pRecord || typeof pRecord !== 'object' || !pRecord._DisplaySettings)
	{
		return pRecord;
	}
	const tmpDisplay = pRecord._DisplaySettings;
	const tmpSettings = (pRecord.Settings && typeof pRecord.Settings === 'object') ? Object.assign({}, pRecord.Settings) : {};
	for (const tmpKey of LIST_KEYS)
	{
		tmpSettings[tmpKey] = textToList(tmpDisplay[tmpKey]);
		if (tmpSettings[tmpKey].length === 0)
		{
			delete tmpSettings[tmpKey];
		}
	}
	// Statuses are persisted as ListDefault rows via the /Statuses endpoint (the
	// PSRS wiring captures the display text before this runs and PUTs it after the
	// save). Strip any legacy status arrays so they never live in Settings again.
	for (const tmpKey of STATUS_LIST_KEYS)
	{
		delete tmpSettings[tmpKey];
	}
	for (const tmpKey of CSV_LIST_KEYS)
	{
		tmpSettings[tmpKey] = csvToList(tmpDisplay[tmpKey]);
		if (tmpSettings[tmpKey].length === 0)
		{
			delete tmpSettings[tmpKey];
		}
	}
	// Endpoint-backed multi-selects (e.g. AllowedProjectTypeIDs) are persisted via
	// their own endpoint (the PSRS wiring captures the display value before this runs and
	// PUTs it after the save). Strip any legacy values so they never live in Settings again.
	for (const tmpKey of ENDPOINT_CSV_KEYS)
	{
		delete tmpSettings[tmpKey];
	}
	// DefaultDates are persisted as *TypeDefaultDate rows via the /DefaultDates
	// endpoint (the PSRS wiring captures the display rows before this runs and PUTs
	// them after the save). Never persist them in Settings.
	delete tmpSettings.DefaultDates;
	pRecord.Settings = tmpSettings;
	delete pRecord._DisplaySettings;
	return pRecord;
}

/**
 * Translate a /Statuses endpoint payload ({ Available, RequiresChangeOrder })
 * into the editor's comma-joined `_DisplaySettings` value — the wire format the
 * status `Select2Multiple` (free-text chip) inputs bind to.
 *
 * @param {{Available?: Array<string>, RequiresChangeOrder?: Array<string>} | null | undefined} pPayload
 * @returns {{AvailableStatusList: string, StatusesRequiringChangeOrder: string}}
 */
function statusPayloadToDisplay(pPayload)
{
	const tmpPayload = (pPayload && typeof pPayload === 'object') ? pPayload : {};
	return {
		AvailableStatusList: listToCsv(tmpPayload.Available),
		StatusesRequiringChangeOrder: listToCsv(tmpPayload.RequiresChangeOrder),
	};
}

/**
 * Translate the editor's comma-joined `_DisplaySettings` status values back into a
 * /Statuses endpoint payload.
 *
 * @param {{AvailableStatusList?: string, StatusesRequiringChangeOrder?: string} | null | undefined} pDisplay
 * @returns {{Available: Array<string>, RequiresChangeOrder: Array<string>}}
 */
function statusDisplayToPayload(pDisplay)
{
	const tmpDisplay = (pDisplay && typeof pDisplay === 'object') ? pDisplay : {};
	return {
		Available: csvToList(tmpDisplay.AvailableStatusList),
		RequiresChangeOrder: csvToList(tmpDisplay.StatusesRequiringChangeOrder),
	};
}

/**
 * Build the editor's `_DisplaySettings` values for the allowed-project-type
 * list from a `/ContractType/:id/AllowedProjectTypes` response:
 *   - `AllowedProjectTypeIDs`       — comma-joined IDs; the Edit picker binds it.
 *   - `AllowedProjectTypeNamesText` — comma-joined Names; the read-only View binds it.
 *
 * @param {Array<string> | null | undefined} pIDs   - IDProjectType strings from the endpoint.
 * @param {Array<string> | null | undefined} pNames - resolved ProjectType Names (View only).
 * @returns {{AllowedProjectTypeIDs: string, AllowedProjectTypeNamesText: string}}
 */
function allowedProjectTypesPayloadToDisplay(pIDs, pNames)
{
	return {
		AllowedProjectTypeIDs: listToCsv(pIDs),
		AllowedProjectTypeNamesText: listToCsv(pNames),
	};
}

/**
 * Translate the editor's comma-joined `_DisplaySettings.AllowedProjectTypeIDs`
 * back into a flat array. The save provider passes this through
 * `_normalizeToProjectTypeIDs` and PUTs `{ IDs: [...] }` to `/AllowedProjectTypes`
 * (the endpoint's bodyParser rejects a bare top-level array under mapParams).
 *
 * @param {{AllowedProjectTypeIDs?: string} | null | undefined} pDisplay
 * @returns {Array<string>}
 */
function allowedProjectTypesDisplayToPayload(pDisplay)
{
	const tmpDisplay = (pDisplay && typeof pDisplay === 'object') ? pDisplay : {};
	return csvToList(tmpDisplay.AllowedProjectTypeIDs);
}

module.exports = {
	LIST_KEYS,
	STATUS_LIST_KEYS,
	STATUS_DISPLAY_KEY_TO_PURPOSE,
	CSV_LIST_KEYS,
	ENDPOINT_CSV_KEYS,
	listToText,
	textToList,
	listToCsv,
	csvToList,
	defaultDatesToText,
	textToDefaultDates,
	statusPayloadToDisplay,
	statusDisplayToPayload,
	allowedProjectTypesPayloadToDisplay,
	allowedProjectTypesDisplayToPayload,
	toDisplay,
	fromDisplay,
};
